import { Settings2 } from "lucide-react";
import type { MomentCategoryNode } from "../../types";
import { MomentIcon } from "../../lib/momentIcon";

type Props = {
  categories: MomentCategoryNode[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onManage?: () => void;
};

// All selectable descendants of a root (children + deeper), depth-first. For the common 2-level
// tree this is just the direct children.
function collectDescendants(node: MomentCategoryNode): MomentCategoryNode[] {
  const out: MomentCategoryNode[] = [];
  node.children.forEach((child) => {
    out.push(child);
    out.push(...collectDescendants(child));
  });
  return out;
}

function Chip({
  node,
  selected,
  onSelect
}: {
  node: MomentCategoryNode;
  selected: boolean;
  onSelect: (id: string | null) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(selected ? null : node.id)}
      aria-pressed={selected}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition ${
        selected
          ? "border-brand bg-brand text-white"
          : "border-line bg-white text-ink hover:border-blue-200 hover:bg-blue-50"
      }`}
    >
      <MomentIcon name={node.icon} size={14} className={selected ? "text-white" : "text-muted"} />
      {node.name}
    </button>
  );
}

// Two-level accordion: each root (Nếp sinh hoạt / Thói quen) is a section; its descendants are
// selectable chips. Single-select; a childless root is directly selectable.
export function CategoryAccordionPicker({ categories, selectedId, onSelect, onManage }: Props) {
  return (
    <div className="space-y-4">
      {categories.map((root) => {
        const descendants = collectDescendants(root);
        return (
          <section key={root.id}>
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-ink">
              <MomentIcon name={root.icon} size={16} className="text-muted" />
              <span>{root.name}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {descendants.length === 0 ? (
                <Chip node={root} selected={selectedId === root.id} onSelect={onSelect} />
              ) : (
                descendants.map((node) => (
                  <Chip key={node.id} node={node} selected={selectedId === node.id} onSelect={onSelect} />
                ))
              )}
            </div>
          </section>
        );
      })}

      {onManage ? (
        <button
          type="button"
          onClick={onManage}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted hover:text-brand"
        >
          <Settings2 size={15} aria-hidden="true" />
          Quản lý nhóm
        </button>
      ) : null}
    </div>
  );
}
